import React, { useState, useRef, useEffect, useContext } from "react";
import { IoIosStar } from "react-icons/io";
import { FaMapMarkerAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { SearchContext } from "../../../context/SearchContext";
import { FcGoogle } from "react-icons/fc";
import config from "../../../config";
// ===============================================
// ⭐ Helper to render star icons
// ===============================================
const renderStars = (rating) => {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => {
        // Determine the color based on the index and rating
        const fill =
          i < Math.floor(rating)
            ? "text-yellow-500"   // Full Star
            : i < rating
            ? "text-yellow-400"   // Fractional/Half Star
            : "text-gray-300";    // Empty Star

        return (
          <IoIosStar key={i} className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${fill}`} />
        );
      })}
    </div>
  );
};

// ===============================================
// 🏪 SimilarVendorCard Component
// ===============================================
const SimilarVendorCard = ({ vendor }) => {
 
  const apiVendorId = vendor.vendor_id || vendor._id; // Use _id, fall back to vendor_id
  const [fetchedRatingData, setFetchedRatingData] = useState({
    rating: null,
    reviews_count: null,
   // isLoading: true,
  });
  const S3_BASE_URL = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL || "";
  const profileImageUrl = vendor?.photo?.profilePhoto
    ? `${S3_BASE_URL}/${vendor.photo.profilePhoto}`
    : "/img/dogLogo.png";

    // --- 1. STATE MANAGEMENT ---

  // State for NEW Google Rating and Reviews
  const [fetchedGoogleRatingData, setFetchedGoogleRatingData] = useState({
    rating: null,
    reviews_count: null,
  });

  // Consolidated Loading State
  const [isLoading, setIsLoading] = useState(true);  
    
      
    

    // useEffect to fetch live rating
  useEffect(() => {
    // 💡 IMPORTANT: Get token here, assuming it's available globally or locally.
    const authToken = localStorage.getItem("auth_token") || ""; // Adjust key if needed

    if (!apiVendorId) {
      setIsLoading(false);
      return;
    }

    // --- FETCH FLIPS RATING ---
    const fetchFlipsRating = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}/customer/getRating/${apiVendorId}`,
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );

        const data = await response.json();

        if (response.ok && data.status && data.data && data.data.length > 0) {
          const { average_rating, reviews_count } = data.data[0];
          setFetchedRatingData({ rating: average_rating, reviews_count: reviews_count });
        } else {
          setFetchedRatingData({ rating: 0, reviews_count: 0 });
        }
      } catch (error) {
        console.error("Error fetching vendor Flips rating:", error);
        setFetchedRatingData({ rating: 0, reviews_count: 0 });
      }
    };

    // Execute both fetches and set loading to false after both are done
    // Use Promise.allSettled to ensure both complete regardless of individual success/fail
    setIsLoading(true);
   // Promise.allSettled([fetchFlipsRating(), fetchGoogleRating()])
    Promise.allSettled([fetchFlipsRating()])
        .finally(() => setIsLoading(false));

  }, [apiVendorId]);

  // --- 3. DISPLAY VARIABLES (FLIPS) ---
  const vendorRating = fetchedRatingData.rating || 0;
  const reviewCount = fetchedRatingData.reviews_count || 0;

  // --- 4. DISPLAY VARIABLES (NEW GOOGLE FETCH) ---
  const googleRating = fetchedGoogleRatingData.rating || 0;
  const googleReviewCount = fetchedGoogleRatingData.reviews_count || 0;


  const handleCardClick = async () => {
    if (!vendor._id) return;

    try {
      const tokenResponse = await fetch(
        `${config.API_BASE_URL}/customer/shareVendorProfile`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ vendor_id: vendor.vendor_id }),
        }
      );

      const tokenData = await tokenResponse.json();

      if (!tokenResponse.ok || !tokenData?.data?.token) {
        throw new Error(tokenData.message || "Token generation failed");
      }

      // Retrieve vendor info from localStorage
      const vendorName =
        localStorage.getItem("vendor_name") || vendor?.name || "vendor";
      const vendorBrand =
        localStorage.getItem("vendor_brand") || vendor?.brand || "brand";

      const formattedVendorName = vendorName.replace(/\s+/g, "-").toLowerCase();
      const formattedVendorBrand = vendorBrand
        .replace(/\s+/g, "-")
        .toLowerCase();

      const token = tokenData.data.token;

      const name = vendor?.profile.name?.replace(/\s+/g, "-").toLowerCase();
      const company = vendor?.profile.brand?.replace(/\s+/g, "-").toLowerCase();

      // ✅ Open vendor profile in new tabnp
      const url = `/${token}/${name}-${company}`;
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error during vendor navigation:", error.message);
    }
  };

     let ratingDisplay;

  if (isLoading) {
      // Show loading pulse while fetching all data
      ratingDisplay = (
        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
      );
  } 
  // ⭐️ PRIORITY 1: NEWLY FETCHED GOOGLE RATING ⭐️
  else if (googleRating > 0 && googleReviewCount > 0) {

      ratingDisplay = (
          <div className="flex items-center gap-1.5 text-blue-700">
              {/* Google Icon */}
              <FcGoogle className="w-3 h-3 text-blue-600 flex-shrink-0" />
              
              {/* Numerical Rating */}
              <p className="text-sm font-semibold">
                  {googleRating.toFixed(1)}
              </p>
              
              {/* Stars (Using the existing Flips-style renderStars) */}
              <div className="flex items-center space-x-0.5"> 
                  {renderStars(googleRating)} 
              </div>
              
              {/* Review Count */}
              <p className="text-xs text-gray-500">
                  ({googleReviewCount.toLocaleString()} reviews)
              </p>
          </div>
      );
  }
  // ⭐️ PRIORITY 2: FLIPS RATING CHECK ⭐️
  else if (reviewCount > 0 && vendorRating > 0) {
      // Display the actual Flips rating
      ratingDisplay = (
          <div className="flex items-center gap-1.5">
              <p className="text-sm font-semibold text-gray-800">
                  {vendorRating.toFixed(1)}
              </p>
              
              {/* Stars */}
              <div className="flex items-center space-x-0.5"> 
                  {renderStars(vendorRating)} 
              </div>
              
              <p className="text-xs text-gray-500">({reviewCount.toLocaleString()} reviews)</p>
          </div>
      );
  } 
  // ⭐️ PRIORITY 3: NEW ON FLIPS ⭐️
  else {
      // Display "New on Flips"
      ratingDisplay = (
          <div className="flex items-center text-sm font-semibold text-[#FF6216]">
              <span>New on Flips</span>
          </div>
      );
  }
  return (
    <div
      onClick={handleCardClick}
      className="flex-shrink-0 w-64 border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden bg-white group cursor-pointer"
      role="link"
    >
      <div className="relative h-20 bg-gradient-to-r from-pink-500 to-orange-500">
        <img
          src={profileImageUrl}
          alt={`${
            vendor?.profile?.brand || vendor?.profile?.companyName
          } Profile`}
          className="absolute bottom-[-24px] left-3 w-16 h-16 rounded-full border-4 border-white object-cover shadow-md transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/img/dogLogo.png";
          }}
        />
      </div>

      <div className="pt-8 p-4">
        <h4 className="text-lg font-bold text-[#000048] truncate">
          {vendor?.profile?.brand || vendor?.profile?.companyName || "Vendor"}
        </h4>

        <p className="text-xs text-gray-500 mt-1 flex items-center">
          <FaMapMarkerAlt className="w-3 h-3 mr-1 text-red-500" />
          <span className="truncate">
            {vendor?.address?.officeAddress?.full_address || "Address N/A"}
          </span>
        </p>

        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
          
           {ratingDisplay}

        </div>
      </div>
    </div>
  );
};

// ===============================================
// 🧭 SimilarVendorsSection Component (Main Export)
// ===============================================
const SimilarVendorsSection = ({ mainVendorData }) => {
  const { fetchVendors } = useContext(SearchContext);
  const [similarVendors, setSimilarVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mainVendorPincode = mainVendorData?.address?.officeAddress?.pincode;
  const currentVendorId = mainVendorData?._id;
  const radius = 20;

  useEffect(() => {
    const loadSimilarVendors = async () => {
      if (!mainVendorPincode) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const result = await fetchVendors(radius, mainVendorPincode);

        if (result) {
          const newVendors = result.data || result;
          const filteredVendors = Array.isArray(newVendors)
            ? newVendors.filter((v) => v._id !== currentVendorId)
            : [];
          setSimilarVendors(filteredVendors);
        } else {
          setError("Failed to load nearby vendors.");
        }
      } catch (err) {
        console.error("Error loading similar vendors:", err);
        setError("Failed to load nearby vendors.");
      } finally {
        setLoading(false);
      }
    };

    loadSimilarVendors();
  }, [mainVendorPincode, currentVendorId]);


  // Scroll logic
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 5);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 5);
    }
  };

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const CARD_WIDTH_PLUS_SPACING = 256 + 16;
      const scrollDistance = CARD_WIDTH_PLUS_SPACING * 4;
      const newScrollLeft =
        direction === "left"
          ? scrollRef.current.scrollLeft - scrollDistance
          : scrollRef.current.scrollLeft + scrollDistance;

      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    let timer;
    if (!loading && similarVendors.length > 0) {
      timer = setTimeout(checkScrollPosition, 100);
    }
    window.addEventListener("resize", checkScrollPosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, [similarVendors, loading]);

  // ===============================================
  // 🔄 Render States
  // ===============================================
  if (loading)
    return (
      <div className="py-8 bg-gray-50 rounded-2xl shadow-inner mt-8 text-center text-gray-500">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
        <p className="mt-2">Finding similar vendors...</p>
      </div>
    );

  if (error)
    return (
      <div className="py-8 bg-gray-50 rounded-2xl shadow-inner mt-8 text-center text-red-500">
        <p>⚠️ {error}</p>
      </div>
    );

  if (similarVendors.length === 0)
    return (
      <div className="py-8 bg-gray-50 rounded-2xl shadow-inner mt-8 text-center text-gray-500">
        <p>No similar vendors found in this area.</p>
      </div>
    );

  // ===============================================
  // ✅ Render Main Section
  // ===============================================
  return (
    <div className="py-8 bg-gray-50 rounded-2xl shadow-inner mt-8">
      <div className="px-4 sm:px-6 lg:px-8">
        <h2 className="text-xl font-bold text-[#000048] mb-3">
          You may also like
        </h2>
        <p className="text-gray-600 mb-6 text-sm">
          Check out other highly-rated service providers in your area.
        </p>
      </div>

      <div className="relative">
        {showLeftArrow && (
          <button
            onClick={() => handleScroll("left")}
            className="absolute z-10 left-0 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg border hover:bg-gray-100 transition duration-150 ml-4"
            aria-label="Scroll Left"
          >
            <FaChevronLeft className="w-4 h-4 text-[#000048]" />
          </button>
        )}

        <div
          ref={scrollRef}
          className="flex overflow-x-auto space-x-4 sm:space-x-6 pb-4 scroll-smooth"
          onScroll={checkScrollPosition}
          style={{ scrollbarWidth: "none" }}

        >
          <style jsx="true">{`
            .flex.overflow-x-auto::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {similarVendors.map((vendor) => (
            <SimilarVendorCard
              key={vendor.vendor_id || vendor._id}
              vendor={vendor}
            />
          ))}
        </div>

        {showRightArrow && (
          <button
            onClick={() => handleScroll("right")}
            className="absolute z-10 right-0 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg border hover:bg-gray-100 transition duration-150 mr-4"
            aria-label="Scroll Right"
          >
            <FaChevronRight className="w-4 h-4 text-[#000048]" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SimilarVendorsSection;
